/**
 * PLP + predictive search: same product card HTML as algolia-listing hits.
 * Swatch colour labels resolve via `setAlgoliaSwatchColorLookup` (PLP passes metaobject map).
 */

import { formatMoney } from '@shopify/theme-currency';
import { algoliaPriceToShopifyCents } from 'lib/algolia/constants';
var algoliaSwatchColorLookup = {};
export function setAlgoliaSwatchColorLookup(map) {
  algoliaSwatchColorLookup = map && typeof map === 'object' ? map : {};
}
function moneyFormat(value) {
  var _window$theme;
  var n = Number(value);
  if (Number.isNaN(n)) return '';
  return formatMoney(n, (_window$theme = window.theme) === null || _window$theme === void 0 ? void 0 : _window$theme.settingCurrencyFormat);
}
function productCardPercentOffLabel(percent) {
  var _window$theme2;
  var tpl = ((_window$theme2 = window.theme) === null || _window$theme2 === void 0 || (_window$theme2 = _window$theme2.strings) === null || _window$theme2 === void 0 ? void 0 : _window$theme2.productCardPercentOff) || '__PCT__% Off';
  return String(tpl).replace(/__PCT__/g, String(percent));
}
function escapeHtml(str) {
  var el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}
function firstNonEmpty() {
  for (var i = 0; i < arguments.length; i++) {
    var _ref;
    var s = String((_ref = i < 0 || arguments.length <= i ? undefined : arguments[i]) !== null && _ref !== void 0 ? _ref : '').trim();
    if (s) return s;
  }
  return '';
}
function algoliaRecordDisplayTitle(rec) {
  var _rec$meta;
  return firstNonEmpty(rec === null || rec === void 0 || (_rec$meta = rec.meta) === null || _rec$meta === void 0 || (_rec$meta = _rec$meta.content) === null || _rec$meta === void 0 ? void 0 : _rec$meta.product_name_override, rec === null || rec === void 0 ? void 0 : rec.title);
}
function algoliaProductCardUiStrings() {
  var _window$theme3;
  var t = ((_window$theme3 = window.theme) === null || _window$theme3 === void 0 ? void 0 : _window$theme3.strings) || {};
  return {
    viewCta: String(t.productCardViewCta || 'View'),
    regularPrice: String(t.productCardRegularPrice || 'Regular price'),
    sale: String(t.productCardTagSale || 'Sale'),
    saleSr: String(t.productCardBadgeSaleAria || 'On sale'),
    soldOut: String(t.productCardSoldOutLabel || 'Sold Out'),
    soldOutSr: String(t.productCardBadgeSoldOutAria || 'Out of stock'),
    lowStock: String(t.productCardLowStockLabel || 'Low stock'),
    lowStockSr: String(t.productCardBadgeLowStockAria || 'Low stock')
  };
}
function swatchColorForAlgoliaItem(item) {
  var _ref2, _item$hex, _item$value, _algoliaSwatchColorLo, _algoliaSwatchColorLo2;
  var fromHit = String((_ref2 = (_item$hex = item.hex) !== null && _item$hex !== void 0 ? _item$hex : item.swatch_color) !== null && _ref2 !== void 0 ? _ref2 : '').trim();
  if (fromHit) return fromHit;
  var label = String((_item$value = item.value) !== null && _item$value !== void 0 ? _item$value : '').trim();
  if (!label) return '';
  return String((_algoliaSwatchColorLo = (_algoliaSwatchColorLo2 = algoliaSwatchColorLookup[label]) === null || _algoliaSwatchColorLo2 === void 0 ? void 0 : _algoliaSwatchColorLo2.color) !== null && _algoliaSwatchColorLo !== void 0 ? _algoliaSwatchColorLo : '').trim();
}
function swatchImageForAlgoliaItem(item) {
  var _ref3, _item$swatch_image, _item$value2, _algoliaSwatchColorLo3, _algoliaSwatchColorLo4;
  var fromHit = String((_ref3 = (_item$swatch_image = item.swatch_image) !== null && _item$swatch_image !== void 0 ? _item$swatch_image : item.swatchImage) !== null && _ref3 !== void 0 ? _ref3 : '').trim();
  if (fromHit) return fromHit;
  var label = String((_item$value2 = item.value) !== null && _item$value2 !== void 0 ? _item$value2 : '').trim();
  if (!label) return '';
  return String((_algoliaSwatchColorLo3 = (_algoliaSwatchColorLo4 = algoliaSwatchColorLookup[label]) === null || _algoliaSwatchColorLo4 === void 0 ? void 0 : _algoliaSwatchColorLo4.image) !== null && _algoliaSwatchColorLo3 !== void 0 ? _algoliaSwatchColorLo3 : '').trim();
}
function productUrlFromHit(hit) {
  var _hit$objectID;
  var u = hit.url && String(hit.url).trim();
  var base = u || (hit.handle ? "/products/".concat(hit.handle) : '#');
  if (base === '#') return base;
  var variantId = String((_hit$objectID = hit.objectID) !== null && _hit$objectID !== void 0 ? _hit$objectID : '').trim();
  if (variantId) return "".concat(base, "?variant=").concat(variantId);
  return base;
}
function jsonForInlineScript(obj) {
  return JSON.stringify(obj).replace(/</g, '\\u003c');
}
function algoliaPictureSpec(url, alt) {
  var u = String(url || '').trim();
  if (!u) return null;
  return {
    src: u,
    w: 1,
    h: 1,
    alt: alt || ''
  };
}
function buildVariantsFromSwatchesHit(hit) {
  var _hit$swatches, _hit$objectID2;
  var items = (_hit$swatches = hit.swatches) === null || _hit$swatches === void 0 ? void 0 : _hit$swatches.items;
  if (!Array.isArray(items) || items.length === 0) return null;
  var hitVariantId = String((_hit$objectID2 = hit.objectID) !== null && _hit$objectID2 !== void 0 ? _hit$objectID2 : '').trim();
  var variants = {};
  for (var item of items) {
    var _item$variant_id, _item$meta, _hit$meta;
    var id = String((_item$variant_id = item.variant_id) !== null && _item$variant_id !== void 0 ? _item$variant_id : '').trim();
    if (!id) continue;
    var pseudo = {
      id,
      variant_id: id,
      price: item.price,
      compare_at_price: item.compare_at_price,
      image_url: firstNonEmpty(item.image_url, item.image),
      hover_image: item.hover_image,
      available: item.available !== false,
      inventory_quantity: item.inventory_quantity,
      inventory_management: item.inventory_management,
      // Per-variant badge, resolved by the Algolia transform
      // (external/algolia/transform.js) into `badge_icon_url` on each
      // swatch item. GID fallback covers the hit's own variant before a
      // re-index ships the URL. We must NOT blanket-apply the hit badge to
      // every variant, or variants with no badge would wrongly show one.
      badge_icon_url: firstNonEmpty(item.badge_icon_url, id === hitVariantId ? hit.badge_icon_url : ''),
      badge_icon_alt: firstNonEmpty(item.badge_icon_alt, id === hitVariantId ? hit.badge_icon_alt : ''),
      badge_icon: firstNonEmpty(item.badge_icon, (_item$meta = item.meta) === null || _item$meta === void 0 || (_item$meta = _item$meta.content) === null || _item$meta === void 0 ? void 0 : _item$meta.badge_icon, id === hitVariantId ? (_hit$meta = hit.meta) === null || _hit$meta === void 0 || (_hit$meta = _hit$meta.content) === null || _hit$meta === void 0 ? void 0 : _hit$meta.badge_icon : '')
    };
    variants[id] = buildAlgoliaVariantCardJsEntry(pseudo, hit);
  }
  return Object.keys(variants).length ? {
    variants
  } : null;
}
function renderAlgoliaCardSwatches(hit) {
  var _hit$swatches2, _hit$objectID3;
  var swatchCap = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 5;
  var items = (_hit$swatches2 = hit.swatches) === null || _hit$swatches2 === void 0 ? void 0 : _hit$swatches2.items;
  if (!Array.isArray(items) || items.length < 2) return '';
  var currentId = String((_hit$objectID3 = hit.objectID) !== null && _hit$objectID3 !== void 0 ? _hit$objectID3 : '').trim();
  var cap = Math.min(items.length, swatchCap);
  var productUrl = escapeHtml(productUrlFromHit(hit));
  var visibleItems = items.slice(0, cap);
  var ui = algoliaProductCardUiStrings();
  var inner = visibleItems.map(item => {
    var _item$value3, _item$variant_id2;
    var value = String((_item$value3 = item.value) !== null && _item$value3 !== void 0 ? _item$value3 : '').trim();
    var vid = String((_item$variant_id2 = item.variant_id) !== null && _item$variant_id2 !== void 0 ? _item$variant_id2 : '').trim();
    var selected = vid === currentId;
    var unavailable = item.available === false;
    var swatchColor = swatchColorForAlgoliaItem(item);
    var swatchImage = swatchImageForAlgoliaItem(item);
    var style = '';
    if (swatchColor || swatchImage) {
      var bgColor = swatchColor ? "background-color:".concat(escapeHtml(swatchColor), ";") : '';
      var bgImage = swatchImage ? "background-image:url(".concat(escapeHtml(swatchImage), ");") : '';
      style = " style=\"".concat(bgColor).concat(bgImage, "\"");
    }
    var swEscaped = escapeHtml(value);
    var ariaLabel = unavailable ? "".concat(swEscaped, ", ").concat(escapeHtml(ui.soldOutSr)) : swEscaped;
    var unavailableClass = unavailable ? ' card__swatch--disabled' : '';
    return "\n        <div\n          class=\"card__swatch--color-option".concat(swatchImage ? ' card__swatch--color-option--image' : '').concat(selected ? ' selected' : '').concat(unavailableClass, "\"\n          data-swatch=\"").concat(swEscaped, "\"\n          data-variant-id=\"").concat(escapeHtml(vid), "\"\n          ").concat(unavailable ? 'data-swatch-unavailable="true"' : '', "\n          role=\"button\"\n          tabindex=\"0\"\n          aria-disabled=\"false\"\n          aria-label=\"").concat(ariaLabel, "\"\n        >\n          <span class=\"card__swatch-option-label\">\n            <span class=\"card__swatch-option-label-inner").concat(swatchImage ? ' card__swatch-option-label-inner--image' : '', "\"").concat(style, "></span>\n            <span class=\"card__swatch-option-label-visually-hidden\">").concat(swEscaped, "</span>\n          </span>\n        </div>");
  }).join('');
  var more = items.length > swatchCap ? "<div class=\"card__more-colors-link\"><a href=\"".concat(productUrl, "\">+").concat(items.length - swatchCap, "</a></div>") : '';
  return "<div class=\"card__color-swatches\" data-card-color-swatches><div class=\"card__color-swatches__inner\">".concat(inner, "</div>").concat(more, "</div>");
}
function buildAlgoliaVariantCardJsEntry(v, productHit) {
  var _ref4, _v$id, _v$compare_at_price, _ref5, _v$manual_text_badge, _v$promo_message, _v$badge_icon_url, _ref6, _v$badge_icon, _v$meta, _v$badge_icon_alt, _v$hover_image, _ref7, _v$variant_id;
  var id = String((_ref4 = (_v$id = v.id) !== null && _v$id !== void 0 ? _v$id : v.variant_id) !== null && _ref4 !== void 0 ? _ref4 : '');
  var price = algoliaPriceToShopifyCents(v.price);
  var compareAt = algoliaPriceToShopifyCents((_v$compare_at_price = v.compare_at_price) !== null && _v$compare_at_price !== void 0 ? _v$compare_at_price : v.compareAtPrice);
  var available = v.available !== false;
  var invQ = v.inventory_quantity;
  var invM = v.inventory_management;
  var badgeKind = 'none';
  var badgeText = '';
  var badgeSr = '';
  var badgeModifier = '';
  var ui = algoliaProductCardUiStrings();
  var manual = String((_ref5 = (_v$manual_text_badge = v.manual_text_badge) !== null && _v$manual_text_badge !== void 0 ? _v$manual_text_badge : v.manual_badge) !== null && _ref5 !== void 0 ? _ref5 : '').trim();
  if (manual) {
    badgeKind = 'manual';
    badgeText = manual;
    badgeSr = manual;
    badgeModifier = 'manual';
  } else if (compareAt > price) {
    badgeKind = 'sale';
    badgeText = ui.sale;
    badgeSr = ui.saleSr;
    badgeModifier = 'sale';
  } else if (!available) {
    badgeKind = 'sold_out';
    badgeText = ui.soldOut;
    badgeSr = ui.soldOutSr;
    badgeModifier = 'sold-out';
  } else if (invM && Number(invQ) <= 10 && available) {
    badgeKind = 'low_inventory';
    badgeText = ui.lowStock;
    badgeSr = ui.lowStockSr;
    badgeModifier = 'low-stock';
  }
  var promo = String((_v$promo_message = v.promo_message) !== null && _v$promo_message !== void 0 ? _v$promo_message : '').trim();
  var iconUrl = String((_v$badge_icon_url = v.badge_icon_url) !== null && _v$badge_icon_url !== void 0 ? _v$badge_icon_url : '').trim();
  var iconGid = String((_ref6 = (_v$badge_icon = v.badge_icon) !== null && _v$badge_icon !== void 0 ? _v$badge_icon : (_v$meta = v.meta) === null || _v$meta === void 0 || (_v$meta = _v$meta.content) === null || _v$meta === void 0 ? void 0 : _v$meta.badge_icon) !== null && _ref6 !== void 0 ? _ref6 : '').trim();
  var iconAlt = String((_v$badge_icon_alt = v.badge_icon_alt) !== null && _v$badge_icon_alt !== void 0 ? _v$badge_icon_alt : '').trim();
  var primaryUrl = firstNonEmpty(v.image, v.image_url, v.product_image, v.imageUrl, productHit.image, productHit.image_url, productHit.product_image, productHit.imageUrl);
  var secondaryUrl = String((_v$hover_image = v.hover_image) !== null && _v$hover_image !== void 0 ? _v$hover_image : '').trim();
  var title = algoliaRecordDisplayTitle(productHit);
  var primarySpec = algoliaPictureSpec(primaryUrl, title);
  var hoverSpec = algoliaPictureSpec(secondaryUrl, title);
  var variantId = String((_ref7 = (_v$variant_id = v.variant_id) !== null && _v$variant_id !== void 0 ? _v$variant_id : v.id) !== null && _ref7 !== void 0 ? _ref7 : '').trim();
  var productUrl = productUrlFromHit(productHit).split('?')[0];
  var url = variantId ? "".concat(productUrl, "?variant=").concat(variantId) : productUrl;
  return {
    id,
    url,
    price,
    compareAtPrice: compareAt > price ? compareAt : null,
    available,
    inventoryQuantity: invQ !== null && invQ !== void 0 ? invQ : null,
    inventoryManagement: invM !== null && invM !== void 0 ? invM : null,
    promo,
    iconUrl,
    iconGid,
    iconAlt,
    badgeKind,
    badgeText,
    badgeSr,
    badgeModifier,
    primary: primarySpec,
    hover: hoverSpec
  };
}
function buildAlgoliaProductCardVariantsPayload(hit) {
  var _ref8, _ref9, _hit$variant_id, _hit$manual_text_badg, _hit$meta2;
  var fromSwatches = buildVariantsFromSwatchesHit(hit);
  if (fromSwatches) return fromSwatches;
  if (hit.product_card_variants && typeof hit.product_card_variants === 'object') {
    var p = hit.product_card_variants;
    return p.variants ? {
      variants: p.variants
    } : {
      variants: hit.product_card_variants
    };
  }
  if (typeof hit.product_card_variants_json === 'string') {
    try {
      var _p = JSON.parse(hit.product_card_variants_json);
      return _p && _p.variants ? _p : {
        variants: _p
      };
    } catch (_unused) {
      /* use fallback */
    }
  }
  if (Array.isArray(hit.variants) && hit.variants.length > 0) {
    var variantsMap = {};
    for (var row of hit.variants) {
      var v = row;
      var entry = buildAlgoliaVariantCardJsEntry(v, hit);
      if (entry.id) variantsMap[entry.id] = entry;
    }
    if (Object.keys(variantsMap).length > 0) {
      return {
        variants: variantsMap
      };
    }
  }
  var id = String((_ref8 = (_ref9 = (_hit$variant_id = hit.variant_id) !== null && _hit$variant_id !== void 0 ? _hit$variant_id : hit.default_variant_id) !== null && _ref9 !== void 0 ? _ref9 : hit.objectID) !== null && _ref8 !== void 0 ? _ref8 : '');
  if (!id) {
    return {
      variants: {}
    };
  }
  var pseudo = {
    id,
    price: hit.price,
    compare_at_price: hit.compare_at_price,
    image_url: firstNonEmpty(hit.image, hit.image_url, hit.product_image, hit.imageUrl),
    available: hit.available,
    inventory_quantity: hit.inventory_quantity,
    inventory_management: hit.inventory_management,
    manual_text_badge: (_hit$manual_text_badg = hit.manual_text_badge) !== null && _hit$manual_text_badg !== void 0 ? _hit$manual_text_badg : hit.manual_badge,
    promo_message: hit.promo_message,
    badge_icon_url: hit.badge_icon_url,
    badge_icon: (_hit$meta2 = hit.meta) === null || _hit$meta2 === void 0 || (_hit$meta2 = _hit$meta2.content) === null || _hit$meta2 === void 0 ? void 0 : _hit$meta2.badge_icon,
    badge_icon_alt: hit.badge_icon_alt
  };
  return {
    variants: {
      [id]: buildAlgoliaVariantCardJsEntry(pseudo, hit)
    }
  };
}
function metaCollectionSubtitleFromHit(hit) {
  var _hit$meta$content$col, _hit$meta3;
  return String((_hit$meta$content$col = (_hit$meta3 = hit.meta) === null || _hit$meta3 === void 0 || (_hit$meta3 = _hit$meta3.content) === null || _hit$meta3 === void 0 ? void 0 : _hit$meta3.collection) !== null && _hit$meta$content$col !== void 0 ? _hit$meta$content$col : '').trim();
}

/**
 * @param {object} hit — Algolia product hit
 * @param {{ swatchCap?: number }} [options] — `swatchCap` defaults to 5 (PLP). Predictive search uses 3.
 */
export function renderProductCard(hit) {
  var _hit$objectID4, _hit$objectID5, _variantPayload$varia, _ref0, _variantPayload$varia2, _effectiveEntry$price, _effectiveEntry$compa, _effectiveEntry$prima, _effectiveEntry$hover, _hit$hover_image, _ref1, _effectiveEntry$promo, _hit$meta4, _ref10, _effectiveEntry$iconA, _ref11, _hit$id;
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var swatchCap = Number(options.swatchCap);
  var effectiveSwatchCap = Number.isFinite(swatchCap) && swatchCap >= 1 ? Math.floor(swatchCap) : 5;
  var ui = algoliaProductCardUiStrings();
  var url = escapeHtml(productUrlFromHit(hit));
  var titleRaw = algoliaRecordDisplayTitle(hit);
  var title = escapeHtml(titleRaw);
  var titleId = "product-card__title-".concat(escapeHtml(String((_hit$objectID4 = hit.objectID) !== null && _hit$objectID4 !== void 0 ? _hit$objectID4 : '')));
  var linkLabelText = title;
  var titleBlock = title ? "<h3 class=\"card__title\" id=\"".concat(titleId, "\"><span class=\"card__title-name\">").concat(title, "</span></h3>") : '';
  var subtitleRaw = hit.collection_label || metaCollectionSubtitleFromHit(hit) || hit.type || hit.product_type || '';
  var subtitle = escapeHtml(String(subtitleRaw));
  var variantPayload = buildAlgoliaProductCardVariantsPayload(hit);
  var swatchesHtml = renderAlgoliaCardSwatches(hit, effectiveSwatchCap);

  // When the Algolia hit is a hidden variant it is filtered from swatches.items
  // and won't appear in variantPayload. Fall back to the first visible swatch
  // variant so the card shows the correct image, price, and badge on initial load.
  var currentVariantId = String((_hit$objectID5 = hit.objectID) !== null && _hit$objectID5 !== void 0 ? _hit$objectID5 : '').trim();
  var currentVariantEntry = currentVariantId ? variantPayload === null || variantPayload === void 0 || (_variantPayload$varia = variantPayload.variants) === null || _variantPayload$varia === void 0 ? void 0 : _variantPayload$varia[currentVariantId] : null;
  var effectiveEntry = (_ref0 = currentVariantEntry !== null && currentVariantEntry !== void 0 ? currentVariantEntry : Object.values((_variantPayload$varia2 = variantPayload === null || variantPayload === void 0 ? void 0 : variantPayload.variants) !== null && _variantPayload$varia2 !== void 0 ? _variantPayload$varia2 : {})[0]) !== null && _ref0 !== void 0 ? _ref0 : null;
  var effectivePriceCents = (_effectiveEntry$price = effectiveEntry === null || effectiveEntry === void 0 ? void 0 : effectiveEntry.price) !== null && _effectiveEntry$price !== void 0 ? _effectiveEntry$price : algoliaPriceToShopifyCents(Number(hit.price) || 0);
  var effectiveCompareAtCents = (_effectiveEntry$compa = effectiveEntry === null || effectiveEntry === void 0 ? void 0 : effectiveEntry.compareAtPrice) !== null && _effectiveEntry$compa !== void 0 ? _effectiveEntry$compa : algoliaPriceToShopifyCents(Number(hit.compare_at_price) || 0);
  var showCompare = effectiveCompareAtCents > effectivePriceCents;
  var compareHtml = showCompare ? escapeHtml(moneyFormat(effectiveCompareAtCents)) : '';
  var currentHtml = escapeHtml(moneyFormat(effectivePriceCents));
  var salePercent = showCompare && effectiveCompareAtCents > 0 ? Math.round((1 - effectivePriceCents / effectiveCompareAtCents) * 100) : 0;
  var discountHtml = showCompare && salePercent > 0 ? escapeHtml(productCardPercentOffLabel(salePercent)) : '';
  var imageUrl = firstNonEmpty(effectiveEntry === null || effectiveEntry === void 0 || (_effectiveEntry$prima = effectiveEntry.primary) === null || _effectiveEntry$prima === void 0 ? void 0 : _effectiveEntry$prima.src, hit.image, hit.image_url, hit.product_image, hit.imageUrl);
  var secondaryUrl = firstNonEmpty(effectiveEntry === null || effectiveEntry === void 0 || (_effectiveEntry$hover = effectiveEntry.hover) === null || _effectiveEntry$hover === void 0 ? void 0 : _effectiveEntry$hover.src, String((_hit$hover_image = hit.hover_image) !== null && _hit$hover_image !== void 0 ? _hit$hover_image : '').trim());
  var badgeKind = 'none';
  var badgeText = '';
  var badgeSr = '';
  var badgeModifier = '';
  if (effectiveEntry) {
    var _effectiveEntry$badge, _effectiveEntry$badge2, _effectiveEntry$badge3, _effectiveEntry$badge4;
    badgeKind = String((_effectiveEntry$badge = effectiveEntry.badgeKind) !== null && _effectiveEntry$badge !== void 0 ? _effectiveEntry$badge : 'none');
    badgeText = escapeHtml(String((_effectiveEntry$badge2 = effectiveEntry.badgeText) !== null && _effectiveEntry$badge2 !== void 0 ? _effectiveEntry$badge2 : ''));
    badgeSr = escapeHtml(String((_effectiveEntry$badge3 = effectiveEntry.badgeSr) !== null && _effectiveEntry$badge3 !== void 0 ? _effectiveEntry$badge3 : ''));
    badgeModifier = String((_effectiveEntry$badge4 = effectiveEntry.badgeModifier) !== null && _effectiveEntry$badge4 !== void 0 ? _effectiveEntry$badge4 : '');
  }
  var promoRaw = String((_ref1 = (_effectiveEntry$promo = effectiveEntry === null || effectiveEntry === void 0 ? void 0 : effectiveEntry.promo) !== null && _effectiveEntry$promo !== void 0 ? _effectiveEntry$promo : hit.promo_message) !== null && _ref1 !== void 0 ? _ref1 : '').trim();
  var promoHtml = promoRaw ? escapeHtml(promoRaw) : '';
  var iconUrl = String((effectiveEntry === null || effectiveEntry === void 0 ? void 0 : effectiveEntry.iconUrl) || hit.badge_icon_url || '').trim();
  // `||` not `??`: the swatches-path entry sets iconGid to '' (empty, not
  // nullish), so we must fall through to the hit-level GID for it to resolve.
  var iconGid = String((effectiveEntry === null || effectiveEntry === void 0 ? void 0 : effectiveEntry.iconGid) || ((_hit$meta4 = hit.meta) === null || _hit$meta4 === void 0 || (_hit$meta4 = _hit$meta4.content) === null || _hit$meta4 === void 0 ? void 0 : _hit$meta4.badge_icon) || '').trim();
  var iconAlt = escapeHtml(String((_ref10 = (_effectiveEntry$iconA = effectiveEntry === null || effectiveEntry === void 0 ? void 0 : effectiveEntry.iconAlt) !== null && _effectiveEntry$iconA !== void 0 ? _effectiveEntry$iconA : hit.badge_icon_alt) !== null && _ref10 !== void 0 ? _ref10 : ''));
  // A direct URL shows immediately; a GID-only icon stays hidden until the
  // async resolver (card-badge-icon.js) paints it in.
  var iconWrapHidden = !iconUrl;
  var primaryPicture = imageUrl ? "\n        <div class=\"card__picture card__picture--primary\" data-card-primary-picture>\n            <img class=\"responsive-image__image card__product-image card__product-image--primary background-image\" src=\"".concat(escapeHtml(imageUrl), "\" alt=\"").concat(title, "\" width=\"1\" height=\"1\" decoding=\"async\" loading=\"lazy\" data-zoom=\"").concat(escapeHtml(imageUrl), "\" />\n        </div>") : '';
  var hoverPicture = secondaryUrl && "<div class=\"card__picture card__picture--hover\" data-card-hover-picture>\n            <img class=\"responsive-image__image card__product-image card__product-image--hover background-image\" src=\"".concat(escapeHtml(secondaryUrl), "\" alt=\"").concat(title, "\" width=\"1\" height=\"1\" decoding=\"async\" loading=\"lazy\" data-zoom=\"").concat(escapeHtml(secondaryUrl), "\" />\n        </div>");
  var badgeHidden = badgeKind === 'none';
  var variantsScript = jsonForInlineScript(variantPayload);
  return "\n        <li class=\"algolia-hits__item\">\n            <article class=\"card card--product\" data-product-card data-product-id=\"".concat(escapeHtml(String((_ref11 = (_hit$id = hit.id) !== null && _hit$id !== void 0 ? _hit$id : hit.objectID) !== null && _ref11 !== void 0 ? _ref11 : '')), "\">\n                <a href=\"").concat(url, "\" class=\"card__link card__link--cover\" data-product-card-link aria-labelledby=\"").concat(titleId, "\">\n                    <span class=\"visually-hidden\" data-product-card-link-label>").concat(linkLabelText, "</span>\n                </a>\n                <div class=\"card__product-inner\">\n                    <div class=\"card__image-wrapper\">\n                        <a href=\"").concat(url, "\" class=\"card__image-hover-link\" data-product-card-link tabindex=\"-1\" aria-hidden=\"true\"></a>\n                        <div class=\"card__image-overlay-row\">\n                            <div\n                                class=\"card__product-badge").concat(badgeModifier ? " card__product-badge--".concat(badgeModifier) : '', "\"\n                                data-product-card-badge\n                                data-badge-kind=\"").concat(badgeKind, "\"\n                                ").concat(badgeHidden ? 'hidden' : '', "\n                            >\n                                <span class=\"card__product-badge__text\" aria-hidden=\"true\" data-product-card-badge-text>").concat(badgeKind === 'none' ? '' : badgeText, "</span>\n                                <span class=\"visually-hidden\" data-product-card-badge-sr>").concat(badgeKind === 'none' ? '' : badgeSr, "</span>\n                            </div>\n                            <div class=\"card__icon-badge\" data-product-card-icon-wrap ").concat(iconWrapHidden ? 'hidden' : '', ">\n                                <img class=\"card__icon-badge-img\" data-product-card-icon ").concat(iconUrl ? "src=\"".concat(escapeHtml(iconUrl), "\"") : '').concat(!iconUrl && iconGid ? " data-badge-icon-gid=\"".concat(escapeHtml(iconGid), "\" data-badge-icon-alt=\"").concat(iconAlt, "\"") : '', " alt=\"").concat(iconAlt, "\" width=\"80\" height=\"80\" loading=\"lazy\" decoding=\"async\" />\n                            </div>\n                        </div>\n                        <div class=\"card__image-stack\">\n                            ").concat(primaryPicture, "\n                            ").concat(hoverPicture || '<div class="card__picture card__picture--hover" data-card-hover-picture hidden></div>', "\n                        </div>\n                        <span class=\"card__button\">\n                            ").concat(escapeHtml(ui.viewCta), "\n                            <svg class=\"icon button__icon icon__arrow--cta\" aria-hidden=\"true\" focusable=\"false\">\n                                <use xlink:href=\"#icon-arrow--cta\"></use>\n                            </svg>\n                        </span>\n                    </div>\n                    <div class=\"card__content card__content--product\">\n                        <div class=\"card__subtitle\">").concat(subtitle, "</div>\n                        ").concat(titleBlock, "\n                        <div class=\"card__price-row\" data-product-card-price-wrap>\n                            <div class=\"card__price card__price--product\" data-product-card-price>\n                                <span class=\"visually-hidden\" data-compare-text ").concat(showCompare ? '' : 'hidden', ">").concat(escapeHtml(ui.regularPrice), "</span>\n                                <s data-compare-price class=\"card__price--sale\" data-product-card-compare ").concat(showCompare ? '' : 'hidden', ">").concat(compareHtml, "</s>\n                                <span class=\"card__price--original\" data-product-card-current>").concat(currentHtml, "</span>\n                            </div>\n                            <span class=\"card__price-discount\" data-product-card-discount ").concat(showCompare && salePercent > 0 ? '' : 'hidden', ">").concat(discountHtml, "</span>\n                            <span class=\"card__price-promo\" data-product-card-promo ").concat(promoHtml ? '' : 'hidden', ">").concat(promoHtml, "</span>\n                        </div>\n                        ").concat(swatchesHtml || '<div class="card__color-swatches" data-card-color-swatches hidden></div>', "\n                    </div>\n                </div>\n                <script type=\"application/json\" data-product-card-variants>").concat(variantsScript, "</script>\n            </article>\n        </li>");
}